<template>
  <widget-settings-group :title="$t(`settings.titles.${$constants.SIDE_BARS.alarmSettings}`)">
    <field-default-sort-columns-with-template
      v-field="form.sort"
      :columns="sortablePreparedWidgetColumns"
      :template="form.sortTemplate"
      :templates="alarmSortColumnsWidgetTemplates"
      :templates-pending="templatesPending"
      @update:template="updateSortTemplate"
    />
    <field-columns
      v-field="form.widgetColumns"
      :template="form.widgetColumnsTemplate"
      :templates="alarmColumnsWidgetTemplates"
      :templates-pending="templatesPending"
      :label="$t('settings.columnNames')"
      :type="$constants.ENTITIES_TYPES.alarm"
      with-html
      with-template
      with-color-indicator
      @update:template="updateColumnsTemplate"
    />
    <field-resize-column-behavior v-field="form.columns" resizable />
    <field-default-elements-per-page v-field="form.itemsPerPage" />
    <field-info-popup
      v-field="form.infoPopups"
      :columns="form.widgetColumns"
    />
    <field-text-editor-with-template
      :value="form.moreInfoTemplate"
      :template="form.moreInfoTemplateTemplate"
      :title="$t('settings.moreInfosModal')"
      :variables="alarmVariables"
      :templates="alarmMoreInfosWidgetTemplates"
      @input="updateMoreInfo"
    />
    <field-text-editor-with-template
      :value="form.exportPdfTemplate"
      :template="form.exportPdfTemplateTemplate"
      :title="$t('settings.exportPdfTemplate')"
      :variables="exportPdfAlarmVariables"
      :default-value="defaultExportPdfTemplateValue"
      :dialog-props="{ maxWidth: 1070 }"
      :templates="alarmExportToPdfTemplates"
      addable
      removable
      @input="updateExportPdf"
    />
    <field-root-cause-settings v-field="form" />
  </widget-settings-group>
</template>

<script>
import { filter } from 'lodash';

import { ALARM_FIELDS_TO_LABELS_KEYS, ALARM_UNSORTABLE_FIELDS, WIDGET_TEMPLATES_TYPES } from '@/constants';

import { formToWidgetColumns } from '@/helpers/entities/widget/column/form';
import { getWidgetColumnLabel, getWidgetColumnSortable } from '@/helpers/entities/widget/list';

import { formBaseMixin } from '@/mixins/form';
import { alarmVariablesMixin } from '@/mixins/widget/variables/alarm';

import FieldDefaultSortColumnsWithTemplate from '@/components/sidebars/form/fields/default-sort-columns-with-template.vue';
import FieldColumns from '@/components/sidebars/form/fields/columns.vue';
import FieldResizeColumnBehavior from '@/components/sidebars/alarm/form/fields/resize-column-behavior.vue';
import FieldInfoPopup from '@/components/sidebars/alarm/form/fields/info-popup.vue';
import FieldTextEditorWithTemplate from '@/components/sidebars/form/fields/text-editor-with-template.vue';
import FieldDefaultElementsPerPage from '@/components/sidebars/form/fields/default-elements-per-page.vue';
import WidgetSettingsGroup from '@/components/sidebars/partials/widget-settings-group.vue';
import FieldRootCauseSettings from '@/components/sidebars/form/fields/root-cause-settings.vue';

import ALARM_EXPORT_PDF_TEMPLATE from '@/assets/templates/alarm-export-pdf.html';

export default {
  components: {
    FieldRootCauseSettings,
    WidgetSettingsGroup,
    FieldDefaultSortColumnsWithTemplate,
    FieldColumns,
    FieldResizeColumnBehavior,
    FieldInfoPopup,
    FieldTextEditorWithTemplate,
    FieldDefaultElementsPerPage,
  },
  mixins: [
    formBaseMixin,
    alarmVariablesMixin,
  ],
  model: {
    prop: 'form',
    event: 'input',
  },
  props: {
    form: {
      type: Object,
      default: () => ({}),
    },
    templates: {
      type: Array,
      default: () => [],
    },
    templatesPending: {
      type: Boolean,
      default: false,
    },
    datetimeFormat: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    defaultExportPdfTemplateValue() {
      return ALARM_EXPORT_PDF_TEMPLATE;
    },

    alarmSortColumnsWidgetTemplates() {
      return filter(this.templates, { type: WIDGET_TEMPLATES_TYPES.alarmSortColumns });
    },

    alarmColumnsWidgetTemplates() {
      return filter(this.templates, { type: WIDGET_TEMPLATES_TYPES.alarmColumns });
    },

    alarmMoreInfosWidgetTemplates() {
      return filter(this.templates, { type: WIDGET_TEMPLATES_TYPES.alarmMoreInfos });
    },

    alarmExportToPdfTemplates() {
      return filter(this.templates, { type: WIDGET_TEMPLATES_TYPES.alarmExportToPdf });
    },

    preparedWidgetColumns() {
      return formToWidgetColumns(this.form.widgetColumns).map(column => ({
        ...column,

        text: getWidgetColumnLabel(column, ALARM_FIELDS_TO_LABELS_KEYS),
      }));
    },

    sortablePreparedWidgetColumns() {
      return this.preparedWidgetColumns.filter(column => getWidgetColumnSortable(column, ALARM_UNSORTABLE_FIELDS));
    },
  },
  methods: {
    updateSortTemplate(template = '', sort = []) {
      this.updateModel({
        ...this.form,

        sort,
        sortTemplate: template,
      });
    },

    updateColumnsTemplate(template, columns) {
      this.updateModel({
        ...this.form,

        widgetColumnsTemplate: template,
        widgetColumns: columns,
      });
    },

    updateMoreInfo(content, template) {
      this.updateModel({
        ...this.form,

        moreInfoTemplate: content,
        moreInfoTemplateTemplate: template,
      });
    },

    updateExportPdf(content, template) {
      this.updateModel({
        ...this.form,

        exportPdfTemplate: content,
        exportPdfTemplateTemplate: template,
      });
    },
  },
};
</script>
